import { maybe } from '@passionware/monads';
import { ReactNode, useContext } from 'react';
import { appScopeUtils } from '../appScope.types';
import { ResolveRouteAppScope } from '../resolveDualPathAppScope';
import { AppScopeContext } from './AppScopeContext';
import { assertInitializedAppScope } from './internalAppScope';

export type RouteAppScopeProviderProps = {
  /** Absent → the parent scope (global organization) passes through. */
  resolveScope?: ResolveRouteAppScope;
  params: Record<string, string | undefined>;
  pathname: string;
  children: ReactNode;
};

/**
 * Always the same provider element, scoped route or not, so the layout under it
 * (sidebar included) is never remounted by moving between routes.
 */
export function RouteAppScopeProvider({
  resolveScope,
  params,
  pathname,
  children
}: RouteAppScopeProviderProps) {
  const parentScope = maybe.getOrThrow(
    useContext(AppScopeContext),
    'RouteAppScopeProvider must be used within GlobalAppScopeProvider'
  );
  if (!resolveScope) {
    return (
      <AppScopeContext.Provider value={parentScope}>
        {children}
      </AppScopeContext.Provider>
    );
  }
  assertInitializedAppScope(parentScope);
  const scope = resolveScope(params, parentScope.organizationId, pathname);
  appScopeUtils.assertValid(scope);
  return (
    <AppScopeContext.Provider value={scope}>
      {children}
    </AppScopeContext.Provider>
  );
}
